// Structure-based staleness for the Argument Critique soundness half.
// Unlike resultFingerprint.ts (paper-scope Jaccard), the soundness critique
// grounds in the claim structure itself: claim edits (updated_at), evidence
// attach/detach (evidence_count), and link changes all flip STALE. Exact
// match, no threshold — argument graphs are small; every change matters.
import type { Claim, ClaimLink } from "@/hooks/useClaims";

export interface ArgumentFingerprintClaim {
  id: string;
  updated_at: string;
  evidence_count: number;
}

export interface ArgumentFingerprint {
  claims: ArgumentFingerprintClaim[];
  link_ids: string[];
  generated_at: string;
}

/** Normalize timestamps so "Z" vs "+00:00" (backend isoformat vs pydantic
 * serialization) never reads as an edit. Falls back to the raw string for
 * unparseable values. Known bound: Date keeps millisecond precision, so
 * two edits to the SAME claim within the same millisecond compare equal —
 * not a real path for human-edited claims, and any other structural change
 * still flips staleness. */
function normTs(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString();
}

function canonicalize(
  claims: ArgumentFingerprintClaim[],
  linkIds: string[],
): string {
  return JSON.stringify({
    claims: [...claims]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((c) => ({
        id: c.id,
        updated_at: normTs(c.updated_at),
        evidence_count: c.evidence_count,
      })),
    link_ids: [...linkIds].sort(),
  });
}

export function isArgumentStale(
  fp: ArgumentFingerprint | undefined,
  claims: Claim[],
  links: ClaimLink[],
): boolean {
  // Pre-fingerprint results render fresh — matches position-analysis behavior.
  if (!fp) return false;
  return (
    canonicalize(fp.claims, fp.link_ids) !==
    canonicalize(
      claims.map((c) => ({
        id: c.id,
        updated_at: c.updated_at,
        evidence_count: c.evidence_count,
      })),
      links.map((l) => l.id),
    )
  );
}
